/**
 * A KPI that opens the list behind it — `src/primitives/`, `docs/adr/9069`.
 *
 * ONE implementation for every KPI component that can link (`Measure`, the templates' `KpiFigure`,
 * the charts' `StatTile`) and for a screen's own KPI card: a figure with an `href` is a real
 * `<a href>` — middle click and «copy link» give the shareable address, a plain click stays in the
 * app with a history entry — and a figure without one is exactly what it was.
 *
 * How the whole card is the target without nesting the (i) in the link: the anchor wraps the
 * FIGURE only, and a pseudo-element stretches it over the card (`after:absolute after:inset-0`, the
 * card is `relative`). The (i) sits in {@link KpiLinkAbove}, `relative z-[1]`, so it is above the
 * stretched area and outside the anchor: opening the definition never navigates (ADR 9047).
 *
 * The link's accessible name is the card's label and its figure (`aria-labelledby` on the two
 * elements that carry them), not whatever the card also says under the figure. Its accessible
 * DESCRIPTION says where it goes (`aria-describedby` on a visually-hidden sibling of the anchor, so
 * the name and the role are untouched): �Opens the list behind this figure�.
 *
 * Usage, in a component that takes an `href`:
 *
 * ```tsx
 * const link = useKpiLink(href);
 * <div className={`... ${link.rootClass}`}>
 *   <span {...link.labelProps}>{label}</span>
 *   <KpiLinkAbove link={link}><AboutPopover … /></KpiLinkAbove>
 *   <KpiLinkAnchor link={link}><span {...link.valueProps}>{figure}</span></KpiLinkAnchor>
 * </div>
 * ```
 *
 * Layering: `src/primitives/` imports nothing above it, so the in-app navigation (the history entry
 * and the `popstate` the shell listens for) is spelled here too — the same three lines as
 * `navigateInApp` in `src/planning/templates/url.ts`.
 */

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FOCUS_RING_INSET } from './focus';

/** What a KPI component needs to draw its link, or nothing to change when it has none. */
export interface KpiLink {
  /** The address the card opens; `undefined`: the card is not a link and nothing in its markup changes. */
  readonly href: string | undefined;
  /** Classes for the card's root: positions the stretched link and gives hover. Empty without an `href`. */
  readonly rootClass: string;
  /** Spread on the element that holds the label (`{}` without an `href`). */
  readonly labelProps: { readonly id?: string };
  /** Spread on the element that holds the figure (`{}` without an `href`). */
  readonly valueProps: { readonly id?: string };
  readonly labelId: string | undefined;
  readonly valueId: string | undefined;
  /** The id of the visually-hidden description of where the link goes; `undefined` without an `href`. */
  readonly descriptionId: string | undefined;
}

/** The card is the hover target; `surface-technical-row` is the token a hovered table row already uses. */
const ROOT_CLASS = 'relative hover:bg-surface-technical-row';

export function useKpiLink(href: string | undefined): KpiLink {
  const id = useId();
  if (href === undefined) return { href, rootClass: '', labelProps: {}, valueProps: {}, labelId: undefined, valueId: undefined, descriptionId: undefined };
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const descriptionId = `${id}-goes-to`;
  return { href, rootClass: ROOT_CLASS, labelProps: { id: labelId }, valueProps: { id: valueId }, labelId, valueId, descriptionId };
}

/** Follow an in-app address: a history entry and the `popstate` the shell's router listens for. */
function follow(href: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
  scrollToFragment(href);
}

/**
 * A card whose list is on the same page names it with a fragment (`…?a.f.b=v:c#the-list`): a
 * `pushState` does not scroll, so the element the fragment names is brought into view here, once the
 * narrowed view has been drawn. No fragment, or none that exists: nothing moves.
 */
function scrollToFragment(href: string): void {
  const at = href.indexOf('#');
  if (at < 0 || typeof document === 'undefined') return;
  const id = decodeURIComponent(href.slice(at + 1));
  if (id === '') return;
  const reveal = (): void => { document.getElementById(id)?.scrollIntoView({ block: 'start' }); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
  else reveal();
}

/** The figure as a link stretched over its card; its children untouched when the card has no `href`. */
export function KpiLinkAnchor({ link, children, className = 'block' }: {
  readonly link: KpiLink;
  readonly children: ReactNode;
  /** Layout of the anchor's own box (the default stacks the figure over what is under it). */
  readonly className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (link.href === undefined) return <>{children}</>;
  const { href } = link;
  return (
    <>
      <a
        href={href}
        data-kpi-link=""
        aria-labelledby={`${link.labelId ?? ''} ${link.valueId ?? ''}`.trim()}
        aria-describedby={link.descriptionId}
        className={`${className} after:absolute after:inset-0 ${FOCUS_RING_INSET}`}
        onClick={(event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          follow(href);
        }}
      >
        {children}
      </a>
      <span id={link.descriptionId} className="sr-only">{t('kpi.linkTarget')}</span>
    </>
  );
}

/**
 * The (i) button of a linked card is at least 24x24 CSS px (WCAG 2.5.8, axe `target-size`): the (i) is 16px in a
 * label (`AboutPopover` size `sm`) and the stretched link is an adjacent target, so a 16px (i) fails unless 24px
 * of clear space surrounds it. `size-6` gives the hit area, `-m-1` cancels the 4px it adds on each side so the
 * label row keeps its height and the icon stays where it was. Spelled as descendant variants because the
 * button is `AboutPopover`'s (or `Help`'s) and this is the one place that knows it sits over a stretched link.
 */
export const KPI_INFO_HIT_AREA = '[&_button]:size-6 [&_button]:-m-1';

/** Lifts what must stay clickable on its own — the (i) — above the stretched link. Untouched without an `href`. */
export function KpiLinkAbove({ link, children }: { readonly link: KpiLink; readonly children: ReactNode }): React.JSX.Element {
  if (link.href === undefined) return <>{children}</>;
  return <span className={`relative z-[1] inline-flex ${KPI_INFO_HIT_AREA}`}>{children}</span>;
}
